import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import infoCircleGreyIcon from 'assets/images/icons/info-circle.svg';

import {
  recurrenceIntervalOptions,
  recurrenceTypeOptionsList,
  repeatsOnOptionsList,
  timeUnitOptionsList,
} from '../../../utils';

import { getDisplayValueFromList } from 'components/utils';

import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import DateTimePickerDropdown from 'components/common-components/DateTimePickerDropdown';
import TimePickerDropdown from 'components/common-components/TimePickerDropdown';
import DaysSelector from 'components/common-components/DaysSelector';
import TimeWithUnitField from './TimeWithUnitField';

const RecurringSchedule = ({ values, handleChange }) => {
  const { t } = useTranslation();
  const {
    recurringStartsAt,
    recurringEndsAt,
    recurringFrequency,
    recurringInterval,
    recurringStartTime,
    daysOfWeek,
    repeatsOn,
    gracePeriodTime,
    gracePeriodFormat,
    reviewPeriodTime,
    reviewPeriodFormat,
  } = values;

  const isWeekly = recurringFrequency === 'weekly';
  const isMonthly = recurringFrequency === 'monthly';

  return (
    <div className='w-100 p-3 d-flex flex-column gap-3'>
      {/* Starts At */}
      <div className='row align-items-center'>
        <div className='col-5 d-flex flex-column'>
          <span className='theme-font-inter-semi-bold theme-text-black-1'>
            {t('field_starts_at')}
          </span>
          <span className='theme-text-black-1'>mm/dd/yyyy</span>
        </div>
        <div className='col-7'>
          <DateTimePickerDropdown
            name='recurringStartsAt'
            value={recurringStartsAt}
            onChange={handleChange}
            hideTime
            defaultTime='startOfDay'
          />
        </div>
      </div>

      {/* Frequency Block */}
      <div className='p-3 d-flex flex-column gap-2 theme-background-white-5 rounded-sm'>
        <span className='theme-font-inter-semi-bold theme-text-black-1'>
          {t('text_recurring_frequency')}
        </span>

        <div className='d-flex gap-3 align-items-center flex-wrap'>
          {/* Frequency Type */}
          <MultiLevelSelect
            name='recurringFrequency'
            options={{ children: recurrenceTypeOptionsList }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='d-flex gap-2 align-items-center cursor-pointer'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {t(
                    getDisplayValueFromList(
                      recurrenceTypeOptionsList,
                      recurringFrequency,
                      'select_select',
                    ),
                  )}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />

          {/* Interval */}
          <MultiLevelSelect
            name='recurringInterval'
            options={{ children: recurrenceIntervalOptions }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='d-flex gap-2 align-items-center cursor-pointer'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {recurrenceIntervalOptions.find((o) => o.value === recurringInterval)
                    ?.displayValue ?? t('select_select')}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' alt='chevron' />
              </>
            }
          />

          {/* Label */}
          {recurringFrequency && (
            <div>
              {t(
                {
                  daily: 'text_days_optional_plural',
                  weekly: 'text_weeks_optional_plural',
                  monthly: 'text_months_optional_plural',
                }[recurringFrequency] || '',
              )}
            </div>
          )}

          {/* Weekly */}
          {isWeekly && (
            <div className='d-flex align-items-center gap-3'>
              <span className='theme-font-inter-semi-bold theme-text-black-1'>{t('text_on')}</span>
              <DaysSelector name='daysOfWeek' value={daysOfWeek} onChange={handleChange} />
              <span className='theme-font-inter-semi-bold theme-text-black-1'>
                {t('text_days')}
              </span>
            </div>
          )}

          {/* Monthly */}
          {isMonthly && (
            <div className='d-flex align-items-center gap-3'>
              <span className='theme-font-inter-semi-bold theme-text-black-1'>
                {t('text_repeats_on')}
              </span>
              <MultiLevelSelect
                name='repeatsOn'
                options={{ children: repeatsOnOptionsList }}
                value={[]}
                handleChange={(field, value) => handleChange(field, value)}
                toggleClasses='d-flex gap-2 align-items-center cursor-pointer'
                toggleComponent={
                  <>
                    <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                      {repeatsOn?.[0] ?? t('select_select')}
                    </label>
                    <img src={chevronGreyIcon} className='rotate-180' />
                  </>
                }
              />
            </div>
          )}

          {/* Time */}
          <div className='d-flex align-items-center gap-2'>
            <span className='theme-font-inter-semi-bold theme-text-black-1'>{t('text_at')}</span>
            <TimePickerDropdown
              name='recurringStartTime'
              value={recurringStartTime}
              onChange={handleChange}
              format12Hour
              defaultTime='startOfDay'
            />
          </div>
        </div>
      </div>

      {/* Ends At */}
      <div className='row align-items-center'>
        <div className='col-5 d-flex flex-column'>
          <span className='theme-font-inter-semi-bold theme-text-black-1'>
            {t('field_ends_at')}
          </span>
          <span className='theme-text-black-1'>mm/dd/yyyy</span>
        </div>
        <div className='col-7'>
          <DateTimePickerDropdown
            name='recurringEndsAt'
            value={recurringEndsAt}
            onChange={handleChange}
            hideTime
            defaultTime='endOfDay'
          />
        </div>
      </div>

      {/* Grace Period */}
      <div className='row align-items-center'>
        <span className='col-5 theme-font-inter-semi-bold theme-text-black-1'>
          {t('text_grace_period')}
        </span>
        <div className='col-7'>
          <TimeWithUnitField
            timeName='gracePeriodTime'
            unitName='gracePeriodFormat'
            timeValue={gracePeriodTime}
            unitValue={gracePeriodFormat}
            unitOptions={timeUnitOptionsList}
            handleChange={handleChange}
          />
        </div>
      </div>

      <div className='width-max-content mt-2 d-flex align-items-center gap-2 ms-auto'>
        <img src={infoCircleGreyIcon} />
        <span>{t('text_after_task_starts_time')}</span>
      </div>

      {/* Review Period */}
      <div className='row align-items-center'>
        <span className='col-5 theme-font-inter-semi-bold theme-text-black-1'>
          {t('text_review_period')}
        </span>
        <div className='col-7'>
          <TimeWithUnitField
            timeName='reviewPeriodTime'
            unitName='reviewPeriodFormat'
            timeValue={reviewPeriodTime}
            unitValue={reviewPeriodFormat}
            unitOptions={timeUnitOptionsList}
            handleChange={handleChange}
          />
        </div>
      </div>

      <div className='width-max-content mt-2 d-flex align-items-center gap-2 ms-auto'>
        <img src={infoCircleGreyIcon} />
        <span>{t('text_starts_after_task_submitted')}</span>
      </div>
    </div>
  );
};

RecurringSchedule.propTypes = {
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

export default React.memo(RecurringSchedule);
